import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('No verification token found. Please use the link from your email.');
      return;
    }

    const verify = async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/auth/verify-email?token=${encodeURIComponent(token)}`
        );
        const data = await res.json();
        if (res.ok) {
          setStatus('success');
          setMessage(data.message || 'Email verified successfully. You can now sign in.');
        } else {
          setStatus('error');
          setMessage(data.detail || 'Verification failed. The link may be invalid or expired.');
        }
      } catch {
        setStatus('error');
        setMessage('Something went wrong. Please try again or request a new verification link.');
      }
    };

    verify();
  }, [token]);

  return (
    <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img src="/mah-logo.png" alt="MAH Quantum Research Institute" className="w-12 h-12 object-cover rounded mx-auto" />
          </Link>
        </div>

        <div className="bg-white border border-[#E5E7EB] p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader size={32} className="text-[#1A73E8] animate-spin mx-auto mb-4" />
              <h2 className="font-serif text-xl font-semibold text-[#1F1F1F] mb-2">Verifying your email…</h2>
              <p className="text-[#6B7280] text-sm">Please wait a moment.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <CheckCircle size={28} className="text-green-600" />
              </div>
              <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-3">Email Verified</h2>
              <p className="text-[#4B5563] text-sm leading-relaxed mb-6">{message}</p>
              <Link
                to="/login"
                className="inline-block px-8 py-3 bg-[#1A73E8] text-white text-sm font-semibold hover:bg-[#174EA6] transition-colors"
              >
                Sign In to Your Account
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <XCircle size={28} className="text-red-500" />
              </div>
              <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-3">Verification Failed</h2>
              <p className="text-[#4B5563] text-sm leading-relaxed mb-6">{message}</p>
              <div className="flex flex-col gap-3">
                <Link
                  to="/register"
                  className="inline-block px-8 py-3 bg-[#1A73E8] text-white text-sm font-semibold hover:bg-[#174EA6] transition-colors"
                >
                  Register Again
                </Link>
                <Link
                  to="/login"
                  className="inline-block px-8 py-2.5 border border-[#E5E7EB] text-[#374151] text-sm font-medium hover:bg-[#F5F7FA] transition-colors"
                >
                  Back to Sign In
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
